import { loadCompanyCensusEnv } from "@/env";
import { createApiClient } from "@cakewalk/frontend-api";

/**
 * Thin client for the public Google Places proxy served by `apps/api`
 * (the `api-prod-platform` Lambda) at `/api/places/*`. The Google API key
 * stays server-side — the SPA only ever sees predictions and resolved
 * addresses.
 *
 * The wire types mirror `@cakewalk/google-places-ui` and the route schema in
 * `apps/api/src/routes/registration/public/places.ts`. They're re-declared
 * here (a handful of fields) to avoid pulling a new cross-package dependency
 * into this standalone app; keep them in lockstep with that route if it moves.
 */

export interface AddressPrediction {
  readonly placeId: string;
  readonly description: string;
  readonly mainText: string;
  readonly secondaryText: string;
}

export interface PlaceDetails {
  readonly placeId: string;
  readonly formattedAddress: string;
  readonly addressLine1: string | null;
  readonly addressLine2: string | null;
  readonly city: string | null;
  readonly state: string | null;
  readonly postalCode: string | null;
  readonly country: string | null;
}

/** Public (unauthenticated) client — `/api/places/*` requires no Cognito session. */
function placesClient() {
  const baseUrl = loadCompanyCensusEnv().VITE_API_BASE_URL.replace(/\/$/, "");
  return createApiClient({ baseUrl, requireAuth: false });
}

export async function placesAutocomplete(query: string): Promise<ReadonlyArray<AddressPrediction>> {
  const res = await placesClient().post<{ predictions: AddressPrediction[] }>(
    "/api/places/autocomplete",
    { query, countryCodes: ["us"] },
  );
  return res.predictions;
}

export async function placesDetails(placeId: string): Promise<PlaceDetails> {
  const res = await placesClient().post<{ details: PlaceDetails }>("/api/places/details", {
    placeId,
  });
  return res.details;
}
